'use client'

import { usePathname, useRouter } from 'next/navigation'
import { CSSProperties, useEffect, useRef, useState } from 'react'

import { useCart } from '@/entities/cart'

import { addProductsToCart } from '@/shared/lib/utils/cart'
import { Button, Text } from '@/shared/ui'

import cl from './add-to-basket-timer.module.scss'

const TIMER_DURATION_MS = 12000

type AddToBasketTimerProps = {
	productIdsToCart: string[]
	onComplete: () => void
}

export const AddToBasketTimer = ({ productIdsToCart, onComplete }: AddToBasketTimerProps) => {
	const [isVisible, setIsVisible] = useState(false)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const hasAddedRef = useRef(false)
	const prevIdsKeyRef = useRef('')
	const router = useRouter()
	const pathname = usePathname()
	const { updateCart } = useCart()

	const idsKey = productIdsToCart.join('|')

	useEffect(() => {
		if (!productIdsToCart.length) {
			setIsVisible(false)
			prevIdsKeyRef.current = ''
			if (timerRef.current) {
				clearTimeout(timerRef.current)
				timerRef.current = null
			}
			return
		}

		if (idsKey === prevIdsKeyRef.current) return

		prevIdsKeyRef.current = idsKey
		hasAddedRef.current = false
		setIsVisible(true)

		if (timerRef.current) clearTimeout(timerRef.current)

		const ids = productIdsToCart

		timerRef.current = setTimeout(() => {
			if (hasAddedRef.current) return

			hasAddedRef.current = true
			onComplete()

			addProductsToCart({ productCodes: ids }).finally(() => {
				updateCart()
				if (pathname === '/cart') {
					router.refresh()
				}
			})
		}, TIMER_DURATION_MS)
	}, [idsKey, productIdsToCart, onComplete, router, pathname, updateCart])

	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current)
		}
	}, [])

	const handleCancel = () => {
		if (timerRef.current) clearTimeout(timerRef.current)
		hasAddedRef.current = true
		setIsVisible(false)
		onComplete()
	}

	if (!isVisible) return null

	return (
		<aside className={cl.addToBasketWrap} style={{ '--timer-duration': `${TIMER_DURATION_MS}ms` } as CSSProperties}>
			<div className={cl.addToBasket}>
				<div className={cl.addToBasket__main}>
					<div>
						<Text weight="bold" size="5" className={cl.addToBasket__text}>
							Добавляю в корзину <span className={cl.addToBasket__dot}>.</span>{' '}
							<span className={cl.addToBasket__dot}>.</span> <span className={cl.addToBasket__dot}>.</span>
						</Text>
						<Text size="2">Товар будет ждать вас в корзине</Text>
					</div>
					<Button className={cl.addToBasket__button} onClick={handleCancel}>
						Отменить
					</Button>
				</div>
			</div>
			<div className={cl.addToBasket__progress} />
		</aside>
	)
}
